import { useState, useEffect } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { WhitelistSection } from "../admin/EcommerceSettings";
import { Tooltip } from "../../components/Tooltip";
import Spinner from "../misc/Spinner";

const BOOKING_FEATURE_CONFIG_QUERY = gql`
  query GetBookingFeatureConfig {
    getBookingFeatureConfig {
      cost
      whitelist
      enabled
      location_count
      enabled_location_count
    }
  }
`;

const SET_BOOKING_FEATURE_CONFIG = gql`
  mutation SetBookingFeatureConfig(
    $cost: Float
    $whitelist: [String!]
    $enabled: Boolean
  ) {
    setBookingFeatureConfig(
      cost: $cost
      whitelist: $whitelist
      enabled: $enabled
    )
  }
`;

const BookingSettings = () => {
  const [bookingFeatureConfigState, setBookingFeatureConfigDataState] =
    useState();
  const [isRefetching, setIsRefetching] = useState(false);

  const bookingFeatureConfigResult = useQuery(BOOKING_FEATURE_CONFIG_QUERY, {
    fetchPolicy: "no-cache",
  });
  const [setBookingFeatureConfig, setBookingFeatureConfigResult] = useMutation(
    SET_BOOKING_FEATURE_CONFIG
  );

  const bookingFeatureConfigData =
    bookingFeatureConfigResult.data?.getBookingFeatureConfig;

  const initBookingFeatureConfigDataState = (configData) => {
    if (configData) {
      setBookingFeatureConfigDataState({
        cost: configData.cost,
        whitelist: configData.whitelist,
        enabled: configData.enabled,
      });
    }
  };

  useEffect(() => {
    initBookingFeatureConfigDataState(bookingFeatureConfigData);
  }, [bookingFeatureConfigData]);

  if (bookingFeatureConfigResult.loading || isRefetching) return <Spinner />;

  if (bookingFeatureConfigResult.error || !bookingFeatureConfigState)
    return <p>Error</p>;

  const disableSave =
    setBookingFeatureConfigResult.loading ||
    !bookingFeatureConfigState.cost ||
    bookingFeatureConfigState.cost < 2;

  return (
    <div className="bg-primary p-3 rounded mt-2">
      <h1 className="text-lg brandPrimaryColor font-bold mt-2">
        Booking Controls
      </h1>

      <div className="flex flex-row items-center mt-2 h-8">
        <label
          htmlFor="bookings"
          style={{ width: "30%" }}
          className="text-gray-200 my-auto flex flex-row"
        >
          Bookings
          <Tooltip id="bookings_t" className="ml-1.5 mt-1" color="white">
            Enables or disables the booking feature globally.
            <br />
            Whitelisted users are not affected by this setting..
          </Tooltip>
        </label>
        <input
          style={{ marginRight: 10 }}
          id="bookings"
          className="ml-2"
          type="checkbox"
          checked={bookingFeatureConfigState.enabled}
          onChange={(e) =>
            setBookingFeatureConfigDataState({
              ...bookingFeatureConfigState,
              enabled: e.target.checked,
            })
          }
        />
        <div className="text-gray-200 text-sm">
          ({bookingFeatureConfigData.enabled_location_count}/
          {bookingFeatureConfigData.location_count} locations enabled)
        </div>
      </div>
      <div className="flex flex-row mt-2 h-8">
        <label
          htmlFor="cost"
          style={{ width: "45%" }}
          className="text-gray-200 my-auto flex flex-row"
        >
          Cost
          <Tooltip id="cost_t" className="ml-1.5 mt-1" color="white">
            Cost per 30 minutes.
          </Tooltip>
        </label>
        <input
          id="cost"
          className="ml-2 bg-black brandPrimaryColor px-2 w-full rounded outline-none"
          type="number"
          min={1}
          max={100}
          value={bookingFeatureConfigState.cost}
          onChange={(e) => {
            if (!e.target.value) {
              setBookingFeatureConfigDataState({
                ...bookingFeatureConfigState,
                cost: "",
              });
              return;
            }

            let value = parseInt(e.target.value, 10);

            // Clamp the value between 1 and 100
            if (value < 1) value = 1;
            if (value > 100) value = 100;

            setBookingFeatureConfigDataState({
              ...bookingFeatureConfigState,
              cost: value,
            });
          }}
        />
      </div>

      <WhitelistSection
        whitelist={bookingFeatureConfigState.whitelist}
        updateWhitelist={(updatedWhitelist) =>
          setBookingFeatureConfigDataState({
            ...bookingFeatureConfigState,
            whitelist: updatedWhitelist,
          })
        }
      />

      <hr className="mt-3 border border-gray-500" />

      <button
        className={`mt-3 bg-green-600 text-white text-sm font-bold py-2 px-4 rounded${
          disableSave ? " opacity-70" : ""
        }`}
        disabled={disableSave}
        onClick={async () => {
          const variables = {
            cost: bookingFeatureConfigState.cost,
            whitelist: bookingFeatureConfigState.whitelist,
          };

          if (
            bookingFeatureConfigData.enabled !==
            bookingFeatureConfigState.enabled
          ) {
            variables.enabled = bookingFeatureConfigState.enabled;
          }

          try {
            await setBookingFeatureConfig({ variables });
            alert("Operation Success!");

            setIsRefetching(true);
            try {
              const result = await bookingFeatureConfigResult.refetch();
              initBookingFeatureConfigDataState(
                result.data?.getBookingFeatureConfig
              );
            } finally {
              setIsRefetching(false);
            }
          } catch (e) {
            alert(`Operation Failure! ${e?.message}`);
          }
        }}
      >
        Save
      </button>
    </div>
  );
};

export default BookingSettings;
